import { useEffect } from 'react'
import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { Helmet } from 'react-helmet-async'

export default function Appointment() {
  // Handle client-side navigation for the services section
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    const existingScript = document.querySelector(
      'script[data-jobber="true"]'
    )

    if (existingScript) return

    const script = document.createElement('script')

    script.src =
      'https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js'

    script.async = true

    script.setAttribute('data-jobber', 'true')

    script.setAttribute(
      'clienthub_id',
      'ccbd065d-bdd6-4f24-81ec-265003352865-4760139'
    )

    script.setAttribute(
      'form_url',
      'https://clienthub.getjobber.com/client_hubs/ccbd065d-bdd6-4f24-81ec-265003352865/public/work_request/embedded_work_request_form?form_id=4760139'
    )

    document.body.appendChild(script)

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
    }
  }, [])

  // Added Schema Data for the Appointment Page
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://busybeaverconstruction.com/appointment",
        "url": "https://busybeaverconstruction.com/appointment",
        "name": "Book a Consultation | Busy Beaver Construction",
        "description": "Schedule a free on-site consultation with Busy Beaver Construction for your custom deck, fence, or outdoor living space project in Calgary.",
        "publisher": {
          "@id": "https://busybeaverconstruction.com/#organization"
        }
      },
      {
        "@type": "LocalBusiness",
        "@id": "https://busybeaverconstruction.com/#organization",
        "name": "Busy Beaver Construction",
        "url": "https://busybeaverconstruction.com/",
        "logo": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png",
        "telephone": "+1-403-286-5400",
        "image": "https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg",
        "areaServed": "Calgary"
      },
      {
        "@type": "ContactPage",
        "mainEntity": {
          "@type": "ContactPoint",
          "telephone": "+1-403-286-5400",
          "contactType": "customer service",
          "areaServed": "CA",
          "availableLanguage": "en"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Appointment",
            "item": "https://busybeaverconstruction.com/appointment"
          }
        ]
      }
    ]
  };

  return (
    <main>
      <Helmet>
        <title>
          Book a Consultation | Busy Beaver Construction | Calgary Deck Builder
        </title>

        <meta
          name="description"
          content="Schedule a free on-site consultation with Busy Beaver Construction for your custom deck, fence, or outdoor living space project in Calgary."
        />

        <meta
          name="keywords"
          content="book deck consultation, Calgary deck builder, deck estimate, fence consultation Calgary, outdoor living space consultation"
        />

        <link
          rel="canonical"
          href="https://busybeaverconstruction.com/appointment"
        />

        <meta
          property="og:title"
          content="Book a Consultation | Busy Beaver Construction"
        />

        <meta
          property="og:description"
          content="Request a free, no-obligation consultation with Calgary's premier deck and fence builder."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://busybeaverconstruction.com/appointment"
        />

        <meta
          property="og:image"
          content="https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <link
          rel="preload"
          href="/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg"
          as="image"
        />

        <link
          rel="stylesheet"
          href="https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css"
        />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />

      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img
          src="/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg"
          alt="Custom TimberTech composite deck installation by Busy Beaver Construction in Calgary"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
        />

        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium">
              The Longest Operating Deck Builder in Calgary
            </p>
          </div>
        </div>
      </section>

      {/* Appointment Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container">

          <h1 className="text-center text-5xl lg:text-6xl font-primary mb-10">
            Request an Onsite Estimate
          </h1>

          <div className="max-w-4xl mx-auto">

            <p className="text-center mb-10 text-gray-600 text-lg">
              Request a consultation with Busy Beaver Construction using our
              online form below. Tell us about your project, and we'll be in
              touch to schedule an appointment.
            </p>

            {/* Jobber Form */}
            <div className="border border-gray-200 rounded-xl shadow-sm bg-white p-4 md:p-8">

              <div
                id="ccbd065d-bdd6-4f24-81ec-265003352865-4760139"
                className="min-h-[1200px]"
              ></div>

            </div>

            <div className="mt-10 text-center text-sm text-gray-500">
              <p>
                If you prefer to request a consultation by phone, please call us
                at{' '}
                <a
                  href="tel:4032865400"
                  className="text-beaver-red font-bold"
                >
                  (403) 286-5400
                </a>
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-primary text-center text-gray-900 mb-4">Our Services</h2>
          <p className="text-center text-lg text-gray-500 font-accent mb-12 uppercase tracking-wide">Expert craftsmanship for your outdoor space</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Custom Decks */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg" 
                  alt="Custom Decks" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Decks</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Premium composite, cedar, and pressure-treated wood decks engineered to withstand Calgary's climate.
                </p>
                <a 
                  href="/services/decks" 
                  onClick={(e) => handleNavigation(e, '/services/decks')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Decks <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Pergolas & Privacy Walls */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Privacy Walls/Custom-Cedar-Privacy-wall-with-Stained-Glass-inserts.jpg" 
                  alt="Pergolas & Privacy Walls" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Pergolas & Privacy Walls</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Custom structures designed to provide shade, seclusion, and architectural beauty to your yard.
                </p>
                <a 
                  href="/services/pergolas-and-privacy-walls" 
                  onClick={(e) => handleNavigation(e, '/services/pergolas-and-privacy-walls')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Options <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Gazebo */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Pergola/Cedar-Gazebo-with-shingled-roof.jpg" 
                  alt="Gazebos" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Gazebos</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Elegant, fully covered outdoor structures perfect for dining, hot tubs, and year-round shelter.
                </p>
                <a 
                  href="/services/gazebo" 
                  onClick={(e) => handleNavigation(e, '/services/gazebo')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Gazebos <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Living Spaces */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Other/Living Spaces/Outdoor-covered-Living-Space-on-Mahogany-lake.jpeg" 
                  alt="Outdoor Living Spaces" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Living Spaces</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Complete backyard transformations including outdoor kitchens and multi-function entertainment areas.
                </p>
                <a 
                  href="/services/living-spaces" 
                  onClick={(e) => handleNavigation(e, '/services/living-spaces')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Living Spaces <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Railings */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg" 
                  alt="Deck Railings" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Railings</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Safe, code-compliant aluminum, glass, and composite railing systems to complement your deck.
                </p>
                <a 
                  href="/services/railings" 
                  onClick={(e) => handleNavigation(e, '/services/railings')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Railings <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Fences */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg" 
                  alt="Custom Fences" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Fences</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Durable property fencing in cedar, pressure-treated wood, and maintenance-free materials.
                </p>
                <a 
                  href="/services/fences" 
                  onClick={(e) => handleNavigation(e, '/services/fences')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Fences <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Landscaping */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col md:col-span-2 lg:col-span-3 lg:w-1/3 lg:mx-auto">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Other/Living Spaces/Multi-function-Outdoor-Living-space-with-BBQ-deck-and-lower-seating-and-dining-area.jpg" 
                  alt="Landscaping Services" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Landscaping</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Professional landscaping solutions to integrate your new structure perfectly with your yard.
                </p>
                <a 
                  href="/services/landscaping" 
                  onClick={(e) => handleNavigation(e, '/services/landscaping')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Landscaping <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      <AssociationsAndSuppliers />

      <Footer />
    </main>
  )
}